import React, { useRef } from 'react';
import cx from 'classnames';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, Row } from 'antd';
import { isNil } from 'lodash';

import FormItem from 'components/formik/FormItem';
import SubmitButton from 'components/formik/SubmitButton';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseAlert from 'components/wrappers/BaseAlert';
import ButtonWithTimer from 'components/twoFactorAuth/ButtonWithTimer';
import { loginWithTwoFA } from 'redux/reducers/authenticationSlice';
import {
  LoginTwoFactorAuthRequest,
  ApplicationTypeEnum,
} from 'api/accountApiClient';
import { isAdminRole } from 'utils/userRolesUtils';
import { InvalidTwoFACodeError } from 'constants/ErrorMessages';
import { clearFormError } from 'utils/formUtils';
import MaskedInput from 'components/formik/MaskedInput';
import { codeMask } from 'utils/validationUtils';

import logo from 'assets/img/logo-dark.svg';

import style from '../../AuthorizationPage.module.less';

const requiredMessage = 'Field is required';

interface FormValues {
  code: string;
}

const validationSchema = Yup.object({
  code: Yup.string()
    .trim()
    .min(6, 'Code should be 6 characters minimum')
    .required(requiredMessage)
    .twoFACodeIsInvalidCheck(),
});

type Props = {
  uid: string;
};

export default function MultiFactorAuthSection(props: Props) {
  const { uid } = props;
  const location = useLocation();
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const navigate = useNavigate();

  const handleSubmitError = (result: any) => {
    if (!isNil(result.payload?.errorDetails?.isInvalidCode)) {
      formRef?.current.setFieldValue('_formError', InvalidTwoFACodeError);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };
  const handleSubmit = async (data: FormValues) => {
    const options: LoginTwoFactorAuthRequest = {
      code: data.code,
      authCode: uid,
      applicationType: ApplicationTypeEnum.Backoffice,
    };
    const result = await dispatch(loginWithTwoFA(options));

    if (loginWithTwoFA.fulfilled.match(result)) {
      // redirect to the 1 - location.state (requested URL) OR
      // 2 - to the / home page when account settings progress tracker is completed OR isAdmin
      // 3 - to the /company-profile when account settings progress tracker is uncompleted
      const user = result.payload;

      const pathname =
        isAdminRole({ user }) || user.isAccountSettingsComplete
          ? '/'
          : '/company-profile';
      const { from } = (location.state as any) || { from: { pathname } };

      navigate(from, { replace: true });
    } else {
      handleSubmitError(result);
    }
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={{ code: '' }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {(formik) => (
        <Form
          className={cx(style.authPageForm, style.signInForm)}
          layout="vertical"
        >
          <div className={style.authPageTitle}>
            <div>Log in</div>
            <div className={style.authPageLogo}>
              <img src={logo} alt="myBasePay" />
            </div>
          </div>

          <div className={style.authPageContent}>
            <div className={style.digitText}>
              Enter the 6 digit authentication code sent to your email address.
            </div>
            <FormItem name="code" label="Authentication code *">
              <div className={style.authCodeBox}>
                <MaskedInput
                  name="code"
                  mask={codeMask}
                  placeholder="Enter code"
                  onChange={() => clearFormError(formik, '_formError')}
                />
              </div>
            </FormItem>
            <ButtonWithTimer authCode={uid} />
            <Row gutter={20} align="middle" className={style.submitBox}>
              <Col xs={{ span: 24, order: 2 }} md={{ span: 10, order: 0 }}>
                <SubmitButton
                  data-id="loginBtn"
                  type="primary"
                  size="large"
                  block
                >
                  Log in
                </SubmitButton>
              </Col>
            </Row>
          </div>
        </Form>
      )}
    </Formik>
  );
}
